import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
interface BulletPoint {
  title: string;
  description: string;
}
interface Category {
  name: string;
  summary: string;
  bullets: BulletPoint[];
}
const CATEGORIES: Category[] = [{
  name: 'Sales & Revenue',
  summary: 'Giga manages your pipeline, enriches your leads, drafts outreach, and reports on performance. Every day.',
  bullets: [{
    title: 'Pipeline automation',
    description: 'Sources leads from Apollo, enriches with firmographic data, pushes to HubSpot or Attio, and activates outbound sequences. Hands-free.'
  }, {
    title: 'Call intelligence',
    description: 'Scans call recordings and notes, extracts action items, updates your CRM fields, and flags deals that need attention.'
  }, {
    title: 'Revenue reporting',
    description: 'Pulls live data from Stripe, HubSpot, and your data warehouse. Builds weekly reports with charts, narrative, and clear next actions.'
  }]
}, {
  name: 'Engineering',
  summary: 'Giga triages bugs, drafts PRs, monitors deployments, and keeps your team unblocked across every repo and service.',
  bullets: [{
    title: 'Bug triage & investigation',
    description: 'Monitors error trackers, reproduces issues, identifies root causes in your codebase, and drafts fix PRs with context.'
  }, {
    title: 'Code review assistant',
    description: 'Reviews pull requests for style, security, and correctness. Leaves inline comments and suggests improvements before humans review.'
  }, {
    title: 'Deployment monitoring',
    description: 'Watches your CI/CD pipelines and alerts on failures. Correlates deploy events with error spikes and rolls back if needed.'
  }]
}, {
  name: 'Marketing & Growth',
  summary: 'Giga writes your content, manages your ad accounts, tracks attribution, and publishes across every channel.',
  bullets: [{
    title: 'Content engine',
    description: 'Writes SEO blog posts, launch copy, email campaigns, ad scripts, and social drafts. Publishes directly to your CMS. Repeats on any schedule.'
  }, {
    title: 'Ad intelligence',
    description: 'Pulls spend, CAC, CTR, and ROAS across Meta Ads and Google Ads. Flags underperformers, recommends budget shifts, and drafts new ad copy.'
  }, {
    title: 'Campaign analytics',
    description: "Connects your analytics, ad platforms, and CRM to build unified attribution reports. Identifies what's actually driving conversions."
  }]
}, {
  name: 'Customer Success',
  summary: 'Giga monitors your customers, flags churn risks, drafts responses, and keeps your team ahead of every issue.',
  bullets: [{
    title: 'Ticket intelligence',
    description: 'Monitors support channels, categorizes incoming tickets, drafts responses using your knowledge base, and escalates edge cases.'
  }, {
    title: 'Churn detection',
    description: 'Tracks product usage patterns, NPS scores, and support sentiment. Flags at-risk accounts before they churn and suggests interventions.'
  }, {
    title: 'Customer reporting',
    description: 'Builds QBR decks and health reports automatically. Pulls usage data, support history, and feature adoption into polished summaries.'
  }]
}, {
  name: 'Operations & Finance',
  summary: 'Giga reconciles your data, automates approvals, tracks compliance, and keeps your back office running smoothly.',
  bullets: [{
    title: 'Data reconciliation',
    description: 'Cross-references records across systems — invoices, payments, inventory — and flags discrepancies before they become problems.'
  }, {
    title: 'Approval workflows',
    description: 'Routes purchase orders, expense reports, and access requests through the right approvers. Follows up automatically on stale items.'
  }, {
    title: 'Compliance tracking',
    description: 'Monitors policy adherence across tools and teams. Generates audit-ready reports and alerts on violations in real time.'
  }]
}];
const CYCLE_DURATION = 6000;
export function DoAnythingCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef(Date.now());
  const startTimer = useCallback((index: number) => {
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    startTimeRef.current = Date.now();
    setProgress(0);
    const tick = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min(elapsed / CYCLE_DURATION, 1);
      setProgress(pct);
      if (pct < 1) {
        timerRef.current = requestAnimationFrame(tick);
      } else {
        setActiveIndex((index + 1) % CATEGORIES.length);
      }
    };
    timerRef.current = requestAnimationFrame(tick);
  }, []);
  useEffect(() => {
    startTimer(activeIndex);
    return () => {
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
    };
  }, [activeIndex, startTimer]);
  const handleSelect = (index: number) => {
    setActiveIndex(index);
  };
  const active = CATEGORIES[activeIndex];
  return <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row gap-8 md:gap-12" data-id="element-179">
      {/* Left: Category list */}
      <div className="md:w-[260px] shrink-0 flex flex-col justify-center" data-id="element-180">
        <div className="space-y-1" data-id="element-181">
          {CATEGORIES.map((cat, i) => <button key={cat.name} onClick={() => handleSelect(i)} className="w-full text-left group" data-id="element-182">
              <div className="py-3 md:py-3.5 relative" data-id="element-183">
                <span className={`text-base md:text-lg font-medium transition-colors duration-200 ${i === activeIndex ? 'text-white' : 'text-neutral-500 group-hover:text-neutral-300'}`} data-id="element-184">
                  {cat.name}
                </span>

                {/* Progress bar track */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-neutral-800" data-id="element-185">
                  {i === activeIndex && <motion.div className="absolute top-0 left-0 h-full bg-white" style={{
                width: `${progress * 100}%`
              }} data-id="element-186" />}
                </div>
              </div>
            </button>)}
        </div>
      </div>

      {/* Right: Content card */}
      <div className="flex-1 min-w-0" data-id="element-187">
        <AnimatePresence mode="wait" data-id="element-188">
          <motion.div key={activeIndex} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-5 md:p-10 h-full" data-id="element-189">
            <p className="text-base md:text-2xl font-semibold text-white leading-snug mb-6 md:mb-8" data-id="element-190">
              {active.summary}
            </p>

            <div className="space-y-4 md:space-y-6" data-id="element-191">
              {active.bullets.map((bullet, i) => <div key={i} className="flex gap-4" data-id="element-192">
                  <div className="shrink-0 mt-0.5" data-id="element-193">
                    <CheckCircle2Icon className="w-5 h-5 text-emerald-400" data-id="element-194" />
                  </div>
                  <div data-id="element-195">
                    <h4 className="text-white font-semibold mb-1" data-id="element-196">
                      {bullet.title}
                    </h4>
                    <p className="text-neutral-400 text-sm leading-relaxed font-body" data-id="element-197">
                      {bullet.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}